import { CommerceService } from "@/services/commerce"
import { EinzaGraphiQLService } from "@/services/einzaGraphiQL"

import { checkPermissions } from "../../shared/permissions/permissions"
import { DomainObjects, Operations } from "../../shared/permissions/permissionsConfig"
import { getFilters, mergeStrings } from "../utils/orderHelpers"

export const queryIdmOrders = {
  async idmOrders(_parent, args, context, _info) {
    const { page, size, sort, filterValues, branchId, searchText, internalOrder } = args
    const { user } = context

    const filters = getFilters(filterValues, searchText)

    if (typeof internalOrder === "boolean") {
      filters.push({ field: "internalOrder", operator: "eq", value: String(internalOrder) })
    }

    await checkPermissions({
      user,
      domainObject: DomainObjects.ORDERS,
      operation: Operations.READ,
    })

    const graphiQLQueryObject = [
      {
        method: "orders: findOrdersByBranchNumberAndFilter",
        variables: {
          filters,
          page,
          size,
          sort: sort ? EinzaGraphiQLService.formatSortParam(sort) : "orderDate.desc",
          branchNumber: branchId,
        },
        fields: ["number", "numberOfElements", "totalElements", "totalPages"],
        expandedFields: {
          content: [
            "orderNumberLong: orderNumber",
            "orderNumberShort: orderNumber2",
            "customerNumber",
            "companyName1",
            "companyName2",
            "paymentMethod",
            "orderType",
            "orderDate",
            "admNumber",
            "totalAmount",
            "branchNumber",
            "customerBranchNumber",
            "orderStatus",
            "approvalDate",
            "picklist",
          ],
        },
      },
    ]

    const mappedQuery = EinzaGraphiQLService.formQuery(graphiQLQueryObject)
    const idmOrdersResponse = (await EinzaGraphiQLService.getData(mappedQuery))?.data?.orders

    if (!idmOrdersResponse?.content || idmOrdersResponse?.content?.length === 0)
      return {
        items: [],
        page: 0,
        size: 0,
        total: 0,
        totalPages: 0,
      }

    // Use Set to remove duplicates
    const uniqueCustomerNumbers = [...new Set(idmOrdersResponse.content.map((order) => order?.customerNumber))]

    const orderCustomerNumbers = uniqueCustomerNumbers.map((num) => `"${num}"`).join(",")

    const customers = await getCtCustomers(orderCustomerNumbers, size)

    if (!customers || customers.length === 0) {
      return {
        items: idmOrdersResponse.content,
        page,
        size,
        total: idmOrdersResponse?.totalElements,
        totalPages: idmOrdersResponse?.totalPages,
      }
    }

    const customerMap = new Map(customers.map((customer) => [customer?.customerNumber, customer]))

    const contentItems = idmOrdersResponse.content.map((order) => {
      const customer = customerMap.get(order?.customerNumber)

      return {
        ...order,
        customerCompanyName: mergeStrings([customer?.customerNumber, customer?.customerName], " - ") || "-",
      }
    })

    return {
      items: contentItems,
      page,
      size,
      total: idmOrdersResponse?.totalElements,
      totalPages: idmOrdersResponse?.totalPages,
    }
  },
}

/**
 * @param {?string} numbersString
 * @returns {Promise<?array>}
 */
const getCtCustomers = async (numbersString = "", size = 25) => {
  if (!numbersString || numbersString.length === 0) return null

  const commerceApi = CommerceService.api()
  const customers = (
    await commerceApi
      .customers()
      .get({
        queryArgs: {
          where: `customerNumber in (${numbersString})`,
          limit: size,
        },
      })
      .execute()
  )?.body?.results

  if (!customers || customers?.length === 0) return null

  return customers.map((customer) => {
    return { customerNumber: customer?.customerNumber, customerName: customer?.companyName }
  })
}
